import { Injectable, inject } from '@angular/core';
import { GrpcEvent, GrpcMessage, GrpcMetadata, GrpcRequest, GrpcStatusEvent } from '@ngx-grpc/common';
import { GrpcHandler, GrpcInterceptor } from '@ngx-grpc/core';
import { Observable, of } from 'rxjs';
import { AsesorIaLoader } from '../asesor-ia.loader';

/**
 * Métodos que solo leen. El nombre no basta como regla (hay lecturas sin `Get` como `CalculateSpread` o `Irr`, y
 * escrituras sin verbo típico como `Rescue` o `HideGoal`), así que lo que no calce aquí se trata como escritura.
 */
const LECTURA =
  /^(Get|List|Browse|Search|Find|Count|Exists|Has|Is|Can|Check|Calculate|Simulate|Preview|Estimate|Irr|GoalEarning|Download|Stream|Watch|Load|Fetch)/;

/**
 * Servicios del propio asesor: pasan siempre (R-09, R-24). El asesor nunca opera el chat (FR-E05), así que sus RPC
 * son del cliente; y `GenerateFollowUps` se llama justo al terminar un turno en el que el asesor pudo operar el
 * portal: con la ventana de la guardia quedaría bloqueada.
 */
const SERVICIOS_DEL_ASESOR: ReadonlySet<string> = new Set(['portal_ai_assistant.PortalAiAssistantGrpcService']);

/** `/paquete.Servicio/Metodo` → `paquete.Servicio`. */
export function servicioDeRuta(path: string): string {
  return path.split('/').filter(Boolean)[0] ?? '';
}

/** gRPC `PERMISSION_DENIED`. */
const PERMISO_DENEGADO = 7;

/**
 * Barrera dura del Asesor IA operando el portal: mientras el asesor ejecuta una acción (y un rato después, hasta
 * que el cliente toque la página), ninguna RPC de escritura sale del navegador. Aunque un botón final no se
 * reconozca por su texto (un "Siguiente" que ya registra un depósito), crear, invertir, retirar o confirmar solo
 * ocurre con un clic del cliente.
 *
 * Va registrada ANTES del interceptor global de errores: corta la cadena sin llamar al servidor y devuelve
 * `PERMISSION_DENIED` sin metadata `bp-error-info`, así que no abre el diálogo global (el componente muestra su
 * propio aviso) y el asesor recibe el motivo en el resultado de su acción.
 *
 * Va en el bundle inicial, así que no importa el asesor (que se descarga aparte, T056): le pregunta a lo que entregó
 * la carga. Antes de cargarse, el asesor no puede operar el portal y la guardia no interviene.
 */
@Injectable()
export class AsesorGuardiaEscrituraInterceptor implements GrpcInterceptor {
  private readonly loader = inject(AsesorIaLoader);

  intercept<Q extends GrpcMessage, S extends GrpcMessage>(request: GrpcRequest<Q, S>, next: GrpcHandler): Observable<GrpcEvent<S>> {
    const asesor = this.loader.cargado?.guardia;
    if (!asesor) return next.handle(request);
    if (SERVICIOS_DEL_ASESOR.has(servicioDeRuta(request.path))) return next.handle(request);
    const metodo = request.path.split('/').pop() ?? '';
    if (LECTURA.test(metodo)) return next.handle(request);
    if (!asesor.escrituraBloqueada()) return next.handle(request);
    asesor.registrarBloqueo(request.path);
    return of(new GrpcStatusEvent(PERMISO_DENEGADO, 'Acción reservada al cliente: la confirma con su propio clic.', new GrpcMetadata()));
  }
}
